/* eslint-disable react/prop-types */
import { useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { statusLabel } from './EstimateStatusSelect';
import logger from '../lib/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// localStorage key — namespaced per user so multi-tenant test logins
// don't cross-contaminate the "last seen" cursor.
const LAST_SEEN_KEY = (userId) => `seertech.statusPushLastSeen.${userId || 'anon'}`;
// 45s feels right: fast enough that a teammate's bulk update reaches
// you well before you'd open the dashboard again, but slow enough that
// 100 users polling = ~2.2 RPS server-side. Easy to tune later.
const POLL_INTERVAL_MS = 45_000;
// Module-level dedupe so React Strict Mode's dev double-mount (and any
// future rapid remounts) can't double-fire toasts. Cleared on full page
// reload — fine since the localStorage cursor handles cross-reload
// dedupe by simply advancing past already-seen events.
const seenEventIds = new Set();

function describePush(evt) {
  if (evt.event === 'workflow_status_cleared') {
    return evt.previous_status
      ? `cleared status (was ${statusLabel(evt.previous_status)})`
      : 'cleared status override';
  }
  const next = statusLabel(evt.status);
  return evt.previous_status
    ? `set status to ${next} (was ${statusLabel(evt.previous_status)})`
    : `set status to ${next}`;
}

/**
 * Headless component that polls
 * `/api/estimates/status-overrides/recent?pinned_only=true&since=<ts>`
 * every ~45s while logged in, fires toasts for any new event NOT
 * authored by the current user, then advances the "last seen" cursor in
 * localStorage so we never double-toast across refreshes or tabs.
 *
 * Mounted once at the protected-route root. Renders nothing.
 */
export default function PinnedStatusNotifier() {
  const { user } = useAuth();
  const navigate = useNavigate();
  // Polling guard — Strict Mode mounts effects twice in dev; this ref
  // ensures we never have two timers in flight.
  const tickInFlightRef = useRef(false);

  useEffect(() => {
    if (!user?.id) return undefined;
    // Honour the user's notification preference (v260.58). Default "all"
    // matches pre-preference behaviour; "off" skips polling entirely so
    // nothing fires; "won_lost_only" filters at the client to just the
    // deal-closing events.
    const pushFilter = user?.preferences?.status_push_filter || 'all';
    if (pushFilter === 'off') return undefined;
    const userId = user.id;
    const storageKey = LAST_SEEN_KEY(userId);

    // Bootstrap: if there's no cursor, stamp "now" so we don't toast
    // every historical event in the user's first session.
    let lastSeen = null;
    try {
      lastSeen = localStorage.getItem(storageKey);
      if (!lastSeen) {
        lastSeen = new Date().toISOString();
        localStorage.setItem(storageKey, lastSeen);
      }
    } catch {
      lastSeen = new Date().toISOString();
    }

    let cancelled = false;
    let timerId = null;

    const tick = async () => {
      if (tickInFlightRef.current || cancelled) return;
      tickInFlightRef.current = true;
      try {
        const r = await axios.get(
          `${API_URL}/api/estimates/status-overrides/recent`,
          {
            params: { pinned_only: true, since: lastSeen, limit: 10 },
            withCredentials: true,
          },
        );
        const events = (r.data?.events || []).filter(Boolean);
        if (!events.length) return;

        // Events come newest-first. Iterate oldest-first so the toast
        // stack reads chronologically when multiple land in one tick.
        const fresh = events
          .filter(e => e.by !== userId)          // ignore my own changes
          .filter(e => !seenEventIds.has(e.id))
          .filter(e => {
            // "won_lost_only" suppresses everything except Won / Lost
            // status_set events. workflow_status_cleared and any other
            // status (Draft / In Review / Approved / Confirmed / etc.)
            // are dropped.
            if (pushFilter !== 'won_lost_only') return true;
            return (
              e.event === 'workflow_status_set'
              && (e.status === 'won' || e.status === 'lost')
            );
          })
          .sort((a, b) => new Date(a.at) - new Date(b.at));

        for (const evt of fresh) {
          seenEventIds.add(evt.id);
          const actor = evt.by_name || 'A teammate';
          const target = evt.estimate_name || 'an estimate';
          const action = describePush(evt);
          toast.info(`${actor} ${action} on ${target}`, {
            description: evt.note ? `“${evt.note}”` : 'Pinned estimate updated',
            duration: 8000,
            action: evt.estimate_id
              ? {
                  label: 'Open',
                  onClick: () => navigate(`/workspace/${evt.estimate_id}/overview`),
                }
              : undefined,
          });
        }

        // Advance the cursor to the newest `at` we observed so the next
        // tick only sees strictly-newer events. Use the server's
        // timestamp (not Date.now) to avoid client-clock skew.
        const newestAt = events[0]?.at;
        if (newestAt) {
          lastSeen = newestAt;
          try { localStorage.setItem(storageKey, newestAt); } catch { /* ignore */ }
        }
      } catch (e) {
        // Silent — push toasts are a nice-to-have, not critical path.
        // Logged at debug so we can investigate if reports come in.
        logger.debug?.('PinnedStatusNotifier poll failed', e?.message);
      } finally {
        tickInFlightRef.current = false;
      }
    };

    // Fire once shortly after login, then on the interval. The initial
    // delay lets the auth context settle and avoids racing the user's
    // first dashboard fetch.
    const initialTimeout = setTimeout(tick, 5_000);
    timerId = setInterval(tick, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearTimeout(initialTimeout);
      if (timerId) clearInterval(timerId);
    };
  }, [user?.id, user?.preferences?.status_push_filter, navigate]);

  return null;
}
